/**
 * KioskDevicesTab - shown on LocationDetail under "Kiosks".
 *
 * Lets location supervisors/managers register, re-key, revoke, and
 * delete kiosk terminal devices. Permission: kiosk.device.manage.
 *
 * Provision code is revealed exactly once (one-shot modal). Only the
 * SHA-256 hash is stored on the server.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  ShieldOff,
  Trash2,
  Tv2,
} from "lucide-react";
import { useState } from "react";

import { ModalOverlay } from "@/components/ModalOverlay";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type {
  KioskDevice,
  KioskDeviceCreateRequest,
  KioskDeviceCreatedResponse,
  KioskRegenerateCodeResponse,
  Uuid,
} from "@tilt/admin-sdk";

// ------------------------------------------------------------------ //
// Main tab                                                            //
// ------------------------------------------------------------------ //

export function KioskDevicesTab(props: { locationId: Uuid; canWrite: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [codeReveal, setCodeReveal] = useState<
    KioskDeviceCreatedResponse | KioskRegenerateCodeResponse | null
  >(null);
  const [editTarget, setEditTarget] = useState<KioskDevice | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<KioskDevice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KioskDevice | null>(null);
  const [expanded, setExpanded] = useState<Set<Uuid>>(new Set());

  const kiosks = useQuery({
    queryKey: ["kiosks", props.locationId],
    queryFn: () => apiClient.listKiosks(props.locationId),
    retry: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["kiosks", props.locationId] });
  };

  const toggleExpand = (id: Uuid) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Kiosk Devices</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Customer self-service payment terminals at this location.
          </p>
        </div>
        {props.canWrite && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" /> Register kiosk
          </button>
        )}
      </div>

      {kiosks.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
          {describe(kiosks.error)}
        </div>
      )}

      {regenerateError && (
        <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
          Failed to regenerate provision code: {regenerateError}
        </div>
      )}

      {kiosks.isLoading && <p className="text-xs text-muted-foreground">Loading kiosks...</p>}

      {kiosks.data && kiosks.data.items.length === 0 && (
        <div className="rounded-md border border-dashed p-6 text-center">
          <Tv2 className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">No kiosks registered</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Register a kiosk to set up customer self-service payments.
          </p>
        </div>
      )}

      {kiosks.data && kiosks.data.items.length > 0 && (
        <div className="space-y-2">
          {kiosks.data.items.map((k) => (
            <KioskRow
              key={k.id}
              kiosk={k}
              canWrite={props.canWrite}
              expanded={expanded.has(k.id)}
              onToggle={() => toggleExpand(k.id)}
              onRegenerate={() => {
                setRegenerateError(null);
                void apiClient
                  .regenerateKioskCode(props.locationId, k.id)
                  .then((resp) => {
                    invalidate();
                    setCodeReveal(resp);
                  })
                  .catch((err: unknown) => {
                    setRegenerateError(
                      err instanceof Error ? err.message : "Failed to regenerate code",
                    );
                  });
              }}
              onEdit={() => setEditTarget(k)}
              onRevoke={() => setRevokeTarget(k)}
              onDelete={() => setDeleteTarget(k)}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <CreateDialog
          locationId={props.locationId}
          onClose={() => setCreateOpen(false)}
          onCreated={(resp) => {
            invalidate();
            setCreateOpen(false);
            setCodeReveal(resp);
          }}
        />
      )}

      {editTarget && (
        <EditDialog
          locationId={props.locationId}
          kiosk={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            invalidate();
            setEditTarget(null);
          }}
        />
      )}

      {codeReveal && <ProvisionCodeModal code={codeReveal} onClose={() => setCodeReveal(null)} />}

      {revokeTarget && (
        <RevokeDialog
          locationId={props.locationId}
          kiosk={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoked={() => {
            invalidate();
            setRevokeTarget(null);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteDialog
          locationId={props.locationId}
          kiosk={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            invalidate();
            setDeleteTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// Kiosk row                                                           //
// ------------------------------------------------------------------ //

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  pending_provision: { label: "Awaiting setup", cls: "bg-yellow-100 text-yellow-800" },
  active: { label: "Active", cls: "bg-green-100 text-green-800" },
  revoked: { label: "Revoked", cls: "bg-red-100 text-red-800" },
};

function KioskRow(props: {
  kiosk: KioskDevice;
  canWrite: boolean;
  expanded: boolean;
  onToggle: () => void;
  onRegenerate: () => void;
  onEdit: () => void;
  onRevoke: () => void;
  onDelete: () => void;
}): JSX.Element {
  const k = props.kiosk;
  const badge = STATUS_LABELS[k.status] ?? {
    label: k.status,
    cls: "bg-muted text-muted-foreground",
  };

  return (
    <div className="rounded-md border bg-card">
      <div className="flex items-start justify-between gap-2 p-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          onClick={props.onToggle}
        >
          {props.expanded ? (
            <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Tv2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium">{k.name}</span>
              <span className={cn("rounded-full px-1.5 py-0.5 text-xs font-medium", badge.cls)}>
                {badge.label}
              </span>
              {k.re_key_required_at && (
                <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-800">
                  Re-key required
                </span>
              )}
            </div>
            {k.serial_number && (
              <p className="mt-0.5 text-xs text-muted-foreground">S/N: {k.serial_number}</p>
            )}
          </div>
        </button>

        {props.canWrite && k.status !== "revoked" && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              title="Edit name"
              onClick={props.onEdit}
              className="rounded p-1 hover:bg-muted"
            >
              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <button
              type="button"
              title="Issue new provision code"
              onClick={props.onRegenerate}
              className="rounded p-1 hover:bg-muted"
            >
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <button
              type="button"
              title="Revoke"
              onClick={props.onRevoke}
              className="rounded p-1 hover:bg-muted"
            >
              <ShieldOff className="h-3.5 w-3.5 text-destructive" />
            </button>
          </div>
        )}

        {props.canWrite && k.status === "revoked" && (
          <button
            type="button"
            title="Delete"
            onClick={props.onDelete}
            className="rounded p-1 hover:bg-muted"
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </button>
        )}
      </div>

      {props.expanded && (
        <div className="border-t bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
            <Detail label="ID" value={k.id} mono />
            {k.card_terminal_name && <Detail label="Card terminal" value={k.card_terminal_name} />}
            {k.claimed_at && <Detail label="Claimed" value={fmtDate(k.claimed_at)} />}
            {k.last_token_refreshed_at && (
              <Detail label="Last refresh" value={fmtDate(k.last_token_refreshed_at)} />
            )}
            {k.refresh_token_expires_at && (
              <Detail label="Token expires" value={fmtDate(k.refresh_token_expires_at)} />
            )}
            {k.re_key_required_at && (
              <Detail label="Re-key at" value={fmtDate(k.re_key_required_at)} />
            )}
            {k.revoked_at && <Detail label="Revoked" value={fmtDate(k.revoked_at)} />}
          </dl>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// Create dialog                                                       //
// ------------------------------------------------------------------ //

function CreateDialog(props: {
  locationId: Uuid;
  onClose: () => void;
  onCreated: (resp: KioskDeviceCreatedResponse) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [serial, setSerial] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: KioskDeviceCreateRequest) => apiClient.createKiosk(props.locationId, body),
    onSuccess: props.onCreated,
    onError: (e) => setError(describe(e)),
  });

  return (
    <ModalOverlay ariaLabel="Kiosk device" onClose={props.onClose}>
      <div className="w-full max-w-sm space-y-4 rounded-lg bg-background p-6 shadow-xl">
        <h2 className="text-base font-semibold">Register kiosk</h2>

        <div className="space-y-3">
          <div>
            <label htmlFor="kiosk-create-name" className="mb-1 block text-xs font-medium">
              Name *
            </label>
            <input
              id="kiosk-create-name"
              className="w-full rounded-md border px-2 py-1.5 text-sm"
              placeholder="e.g. Front Lobby"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="kiosk-create-serial" className="mb-1 block text-xs font-medium">
              Serial number (optional)
            </label>
            <input
              id="kiosk-create-serial"
              className="w-full rounded-md border px-2 py-1.5 text-sm"
              placeholder="e.g. SN-12345"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
            />
          </div>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!name.trim() || create.isPending}
            onClick={() =>
              create.mutate({
                name: name.trim(),
                ...(serial.trim() ? { serial_number: serial.trim() } : {}),
              })
            }
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Register
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ------------------------------------------------------------------ //
// Edit dialog                                                         //
// ------------------------------------------------------------------ //

function EditDialog(props: {
  locationId: Uuid;
  kiosk: KioskDevice;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [name, setName] = useState(props.kiosk.name);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiClient.updateKiosk(props.locationId, props.kiosk.id, { name: name.trim() }),
    onSuccess: props.onSaved,
    onError: (e) => setError(describe(e)),
  });

  return (
    <ModalOverlay ariaLabel="Kiosk device" onClose={props.onClose}>
      <div className="w-full max-w-sm space-y-4 rounded-lg bg-background p-6 shadow-xl">
        <h2 className="text-base font-semibold">Edit kiosk</h2>
        <div>
          <label htmlFor="kiosk-edit-name" className="mb-1 block text-xs font-medium">
            Name
          </label>
          <input
            id="kiosk-edit-name"
            className="w-full rounded-md border px-2 py-1.5 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ------------------------------------------------------------------ //
// Provision code modal (one-shot reveal)                              //
// ------------------------------------------------------------------ //

function ProvisionCodeModal(props: {
  code: KioskDeviceCreatedResponse | KioskRegenerateCodeResponse;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const copy = () => {
    setCopyError(false);
    void navigator.clipboard.writeText(props.code.provision_code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        setCopyError(true);
      },
    );
  };

  return (
    <ModalOverlay ariaLabel="Kiosk device" onClose={props.onClose}>
      <div className="w-full max-w-sm space-y-4 rounded-lg bg-background p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500" />
          <div>
            <h2 className="text-base font-semibold">Provision code</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              This code is shown once. Type it into the kiosk setup screen. It cannot be retrieved
              again.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
          <code className="flex-1 text-center font-mono text-2xl font-bold tracking-widest">
            {props.code.provision_code}
          </code>
          <button
            type="button"
            title="Copy to clipboard"
            onClick={copy}
            className="rounded p-1 hover:bg-background"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <ClipboardCopy className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </div>

        {copyError && (
          <p className="text-xs text-destructive">
            Could not copy to clipboard. Please copy the code manually.
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          Expires:{" "}
          {new Date(props.code.provision_code_expires_at).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })}
        </p>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            Done - I saved the code
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ------------------------------------------------------------------ //
// Revoke dialog                                                       //
// ------------------------------------------------------------------ //

function RevokeDialog(props: {
  locationId: Uuid;
  kiosk: KioskDevice;
  onClose: () => void;
  onRevoked: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: () => apiClient.revokeKiosk(props.locationId, props.kiosk.id),
    onSuccess: props.onRevoked,
    onError: (e) => setError(describe(e)),
  });

  return (
    <ModalOverlay ariaLabel="Kiosk device" onClose={props.onClose}>
      <div className="w-full max-w-sm space-y-4 rounded-lg bg-background p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <h2 className="text-base font-semibold">Revoke kiosk</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              <strong>{props.kiosk.name}</strong> will be immediately blocked from accepting
              payments. The next API call from the device will return 401.
            </p>
          </div>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
            className="flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {revoke.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Revoke
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ------------------------------------------------------------------ //
// Delete dialog                                                       //
// ------------------------------------------------------------------ //

function DeleteDialog(props: {
  locationId: Uuid;
  kiosk: KioskDevice;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: () => apiClient.deleteKiosk(props.locationId, props.kiosk.id),
    onSuccess: props.onDeleted,
    onError: (e) => setError(describe(e)),
  });

  return (
    <ModalOverlay ariaLabel="Kiosk device" onClose={props.onClose}>
      <div className="w-full max-w-sm space-y-4 rounded-lg bg-background p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <h2 className="text-base font-semibold">Delete kiosk record</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Permanently removes <strong>{props.kiosk.name}</strong> from the device list. This
              only works for already-revoked devices.
            </p>
          </div>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={del.isPending}
            onClick={() => del.mutate()}
            className="flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {del.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ------------------------------------------------------------------ //
// Helpers                                                             //
// ------------------------------------------------------------------ //

function Detail(props: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div>
      <dt className="text-muted-foreground">{props.label}</dt>
      <dd className={cn("truncate", props.mono && "font-mono")}>{props.value}</dd>
    </div>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
